import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"
import AsyncStorage from "@react-native-async-storage/async-storage"
import {
  OnboardingStudentPersonalInfoInput,
  OnboardingStudentContactInfoInput,
  OnboardingStudentAddressInfoInput,
  OnboardingStudentBackgroundInfoInput,
  StudentUser,
} from "students-api"

type Steps =
  | "personalInfo"
  | "contactInfo"
  | "addressInfo"
  | "backgroundInfo"
  | "availabilityInfo"

interface StudentOnboardingState {
  currentStepId: Steps
  setCurrentStepId: (id: Steps) => void

  hasHydratedFromApi: boolean
  hydrateFromApiUserOnce: (user: StudentUser) => void

  completedSteps: Partial<Record<Steps, boolean>>
  setStepComplete: (step: Steps, done: boolean) => void
  clearCompletedSteps: () => void

  personalInfo: OnboardingStudentPersonalInfoInput
  setPersonalInfo: (info: OnboardingStudentPersonalInfoInput) => void

  contactInfo: OnboardingStudentContactInfoInput
  setContactInfo: (info: OnboardingStudentContactInfoInput) => void

  addressInfo: OnboardingStudentAddressInfoInput
  setAddressInfo: (info: OnboardingStudentAddressInfoInput) => void

  backgroundInfo: OnboardingStudentBackgroundInfoInput
  setBackgroundInfo: (info: OnboardingStudentBackgroundInfoInput) => void

  clearInfo: () => void

  studentType?: StudentUser["studentType"]
}

const defaultPersonalInfo: OnboardingStudentPersonalInfoInput = {
  firstName: "",
  lastName: "",
  birthDate: "",
  gender: null,
}

const defaultContactInfo: OnboardingStudentContactInfoInput = {
  email: "",
  phoneNumberCall: "",
  phoneNumberWhatsapp: "",
  secondaryEmail: null,
  samePhone: true,
}

const defaultAddressInfo: OnboardingStudentAddressInfoInput = {
  postalCode: "",
  houseNumber: "",
  houseNumberAddition: null,
  street: "",
  city: "",
}

const defaultBackgroundInfo: OnboardingStudentBackgroundInfoInput = {
  nationality: null,
  countryOfBirth: null,
  countryOfOrigin: null,
  nativeLanguage: null,
  otherLanguages: [],
  citizenServiceNumber: null,
}

export const useStudentOnboardingStore = create<StudentOnboardingState>()(
  persist(
    (set, get) => ({
      currentStepId: "personalInfo",
      setCurrentStepId: (id) => set({ currentStepId: id }),

      hasHydratedFromApi: false,

      hydrateFromApiUserOnce: (user) => {
        const { hasHydratedFromApi } = get()
        if (hasHydratedFromApi) return

        set((state) => ({
          ...state,
          personalInfo: {
            firstName: user.firstName,
            lastName: user.lastName,
            birthDate: user.birthDate,
            gender: user.gender,
          },
          contactInfo: {
            email: user.email,
            phoneNumberCall: user.phoneNumberCall,
            phoneNumberWhatsapp: user.phoneNumberWhatsapp,
            secondaryEmail: user.secondaryEmail ?? null,
            samePhone: true,
          },
          addressInfo: {
            postalCode: user.postalCode ?? "",
            houseNumber: user.houseNumber ?? "",
            houseNumberAddition: user.houseNumberAddition ?? null,
            street: user.street ?? "",
            city: user.city ?? "",
          },

          backgroundInfo: {
            nationality: user.nationality?.iso ?? null,
            countryOfBirth: user.countryOfBirth?.iso ?? null,
            countryOfOrigin: user.countryOfOrigin?.iso ?? null,
            nativeLanguage: user.nativeLanguage?.iso ?? null,
            otherLanguages: user.otherLanguages?.map((l) => l.iso) ?? [],
            citizenServiceNumber: user.citizenServiceNumber ?? null,
          },
          studentType: user.studentType,
          hasHydratedFromApi: true,
        }))
      },

      completedSteps: {},
      setStepComplete: (step, done) =>
        set((s) => ({ completedSteps: { ...s.completedSteps, [step]: done } })),
      clearCompletedSteps: () => set({ completedSteps: {} }),

      personalInfo: defaultPersonalInfo,
      setPersonalInfo: (info: OnboardingStudentPersonalInfoInput) => {
        set({ personalInfo: info })
      },

      contactInfo: defaultContactInfo,
      setContactInfo: (info: OnboardingStudentContactInfoInput) => {
        set({ contactInfo: info })
      },

      addressInfo: defaultAddressInfo,
      setAddressInfo: (info: OnboardingStudentAddressInfoInput) => {
        set({ addressInfo: info })
      },

      backgroundInfo: defaultBackgroundInfo,
      setBackgroundInfo: (info: OnboardingStudentBackgroundInfoInput) => {
        set({ backgroundInfo: info })
      },

      clearInfo: () => {
        set({
          currentStepId: "personalInfo",
          completedSteps: {},
          hasHydratedFromApi: false,
          personalInfo: defaultPersonalInfo,
          contactInfo: defaultContactInfo,
          addressInfo: defaultAddressInfo,
          backgroundInfo: defaultBackgroundInfo,
        })
      },
    }),
    {
      name: "student-onboarding-storage",
      storage: createJSONStorage(() => AsyncStorage),
    },
  ),
)
